import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { User } from '../models/user';
import { Person } from '../models/person';

import { AppSettings } from '../app.settings';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient) {}

  get(id: string): Promise<User> {
    return new Promise((resolve, reject) => {
      this.http.get(AppSettings.API_ENDPOINT + 'users/id/' + id, AppSettings.DEFAULT_HTTP_OPTIONS)
        .subscribe((response: any) => {
          if (response.data && response.type == 'user') {
            resolve(response.data);
          } else {
            reject(response);
          }
        }, error => reject(error instanceof HttpErrorResponse ? error.error : error));
    });
  }

  sendVerification(type: 'email' | 'sms', destination: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.post(
        AppSettings.API_ENDPOINT + 'users/send_verification',
        { type, destination },
        AppSettings.DEFAULT_HTTP_OPTIONS
      ).subscribe(
        () => resolve(),
        error => reject(error instanceof HttpErrorResponse ? error.error : error)
      );
    });
  }

  verifyCode(type: 'email' | 'sms', destination: string, code: string): Promise<{ token: string }> {
    return new Promise((resolve, reject) => {
      this.http.post<any>(
        AppSettings.API_ENDPOINT + 'users/verify_code',
        { type, destination, code },
        AppSettings.DEFAULT_HTTP_OPTIONS
      ).subscribe(
        (res: any) => {
          // API uses the JSON:API-adjacent { type, data } envelope;
          // for verify_code, data is the token string itself.
          const token = typeof res?.data === 'string' ? res.data : (res?.data?.token ?? res?.token);
          if (!token) { reject(res); return; }
          resolve({ token });
        },
        error => reject(error instanceof HttpErrorResponse ? error.error : error)
      );
    });
  }

  /**
   * Returns true if the username is available, false if taken.
   * Used as an Observable so the component can debounce/distinctUntilChanged.
   */
  checkUsername(username: string): Observable<boolean> {
    return this.http.get<{ type: string; data: boolean }>(
      AppSettings.API_ENDPOINT + 'is_username_unique?username=' + encodeURIComponent(username)
    ).pipe(
      map(res => !!res?.data),
      catchError(() => of(true)) // treat network errors as "let them try" — server still validates
    );
  }

  /**
   * Creates the account.
   *
   * Address fields on the person are optional now — they're collected later
   * when the user enters as owner/trainer/rider/payee on an entry.
   *
   * `verification_tokens` carries the OTP tokens obtained from verifyCode().
   * At least one of `email` or `sms` MUST be a non-empty token — the API
   * rejects the request otherwise. Either of the two slots may be null
   * (the user only had to verify one channel), but not both.
   */
  createUser(
    user: User,
    person: Person,
    password_match: string,
    verification_tokens:
      | { email: string;        sms: string | null }
      | { email: string | null; sms: string        }
  ): Promise<User> {
    const post_data = {
      data: {
        type: 'user_request',
        attributes: {
          user: user,
          person: person,
          password_match: password_match,
          verification_tokens: verification_tokens,
        }
      }
    };

    return new Promise((resolve, reject) => {
      this.http.post(
        AppSettings.API_ENDPOINT + 'users/create/true',
        post_data,
        AppSettings.DEFAULT_HTTP_OPTIONS
      ).subscribe((response: any) => {
        if (response.data && response.type == 'user') {
          resolve(response.data);
        } else {
          reject(response);
        }
      }, error => reject(error instanceof HttpErrorResponse ? error.error : error));
    });
  }

  /**
   * Authenticated. Links a USEF membership to the current user's existing
   * person record. Called from the post-account "Link USEF" step so that
   * USEF lookup never overwrites verified contact info.
   */
  linkUsefMembership(usef_number: string, usef_person_uuid: string, usef_entity_type: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.patch(
        AppSettings.API_ENDPOINT + 'users/usef_membership',
        { usef_number, usef_person_uuid, usef_entity_type },
        AppSettings.DEFAULT_HTTP_OPTIONS
      ).subscribe(
        () => resolve(),
        (error: HttpErrorResponse | any) => {
          if (error instanceof HttpErrorResponse) {
            // status 0 = request never completed (CORS preflight, offline, DNS, etc.).
            // error.error is a raw ProgressEvent in that case, which JSON-stringifies
            // to '{"isTrusted":true}' — useless to surface to users.
            if (error.status === 0) {
              reject("Couldn't reach the server. Please check your connection and try again.");
            } else {
              reject(error.error || error.message || 'Linking your USEF membership failed. Please try again.');
            }
          } else {
            reject(error);
          }
        }
      );
    });
  }

  update(user: User, change_password: boolean, password_match: string, token: string): Promise<User> {
    const put_data = {
      data: {
        type: 'user',
        attributes: { user: user, change_password: change_password, password_match: password_match, token: token }
      }
    };

    return new Promise((resolve, reject) => {
      if (change_password && user.password != password_match) {
        return reject("The same value must be entered in both password fields");
      }

      this.http.put(AppSettings.API_ENDPOINT + 'users', put_data, AppSettings.DEFAULT_HTTP_OPTIONS)
        .subscribe((response: any) => {
          if (response.data && response.type == 'user') {
            resolve(response.data);
          } else {
            reject(response);
          }
        }, error => reject(error instanceof HttpErrorResponse ? error.error : error));
    });
  }
}
